/**
 * Default add-to-cart flow, covering two cases where the buy-buttons block's
 * settings mean nothing else on the page already owns the submit:
 *  - Neither "Skip cart" nor "Clear cart before add to cart" enabled: add via
 *    fetch, then open/refresh our own cart drawer.
 *  - "Skip cart" enabled (Buy Now → straight to checkout), "Clear cart before
 *    add to cart" NOT enabled: add via fetch, then redirect to checkout.
 *    (The combination of both settings together is instead handled by
 *    clear-cart-before-atc.js, which needs to clear the cart first.)
 *
 * Shrine's own product-form JS is expected to own the add-to-cart submit in
 * both cases (AJAX add to cart/add.js, then either a cart:open event our
 * cart-drawer.js listens for, or its own redirect to checkout for skip-cart
 * forms) — but on some product pages (observed: Mints and Toothpaste
 * subscription PDPs, and a Replo landing page sharing the same buy-box
 * markup) it silently fails to intercept the submit at all: the browser
 * does a native POST straight to /cart/add and gets redirected to /cart
 * instead, with no console errors and nothing else in our own JS competing
 * for the event. Since Shrine's product-form code lives on a remote CDN we
 * can't fix it directly, so — same approach already used for cart mutations
 * elsewhere — we stop depending on it and own this submit ourselves.
 *
 * Only acts when the form isn't already handled by clear-cart-before-atc.js
 * (data-clear-cart-before-add="true").
 */
(function () {
  'use strict';

  if (window.__productFormDefaultAddInstalled) return;
  window.__productFormDefaultAddInstalled = true;

  var _fetch = window.__originalFetch || window.fetch.bind(window);

  function cartRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.slice(-1) === '/' ? r : r + '/';
  }

  function isClearCartEnabled(form) {
    var productForm = form.closest('product-form');
    return !!productForm && productForm.getAttribute('data-clear-cart-before-add') === 'true';
  }

  function isSkipCartEnabled(form) {
    return !!form.querySelector('[data-skip-cart="true"]');
  }

  function getStoreCheckoutUrl() {
    var raw = window.routes && window.routes.cart_url;
    if (typeof raw !== 'string' || !raw.length) return '/checkout';
    try {
      var u = new URL(raw, window.location.origin);
      u.pathname = u.pathname.replace(/\/cart\/?$/, '/checkout');
      return u.pathname + u.search;
    } catch (e) {
      return '/checkout';
    }
  }

  function getErrorElements(form) {
    var productForm = form.closest('product-form');
    var wrap = productForm && productForm.querySelector('.product-form__error-message-wrapper');
    var text = wrap && wrap.querySelector('.product-form__error-message');
    return { wrap: wrap, text: text };
  }

  function showError(form, message) {
    var els = getErrorElements(form);
    if (!els.wrap || !els.text) return;
    els.text.textContent = message;
    els.wrap.hidden = false;
  }

  function hideError(form) {
    var els = getErrorElements(form);
    if (els.wrap) els.wrap.hidden = true;
  }

  function setSubmitting(form, isLoading) {
    var buttons = form.querySelectorAll('button[type="submit"], button.product-form__submit, button[name="add"]');
    buttons.forEach(function (btn) {
      var spinner = btn.querySelector('.loading-overlay__spinner');
      if (isLoading) {
        btn.classList.add('loading');
        btn.setAttribute('aria-busy', 'true');
        btn.style.pointerEvents = 'none';
        if (spinner) spinner.classList.remove('hidden');
      } else {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-busy');
        btn.style.pointerEvents = '';
        if (spinner) spinner.classList.add('hidden');
      }
    });
  }

  function addToCart(form) {
    setSubmitting(form, true);
    hideError(form);

    return _fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    })
      .then(function (res) {
        return res.json().catch(function () { return null; }).then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var message = (result.data && (result.data.description || result.data.message))
            || 'Could not add item to cart.';
          showError(form, message);
          return;
        }
        if (isSkipCartEnabled(form)) {
          window.location.href = getStoreCheckoutUrl();
          return;
        }
        if (window.cartDrawerCustom) {
          if (typeof window.cartDrawerCustom.refresh === 'function') window.cartDrawerCustom.refresh();
          if (typeof window.cartDrawerCustom.open === 'function') window.cartDrawerCustom.open();
        }
      })
      .catch(function (err) {
        console.error('[product-form-default-add]', err);
        showError(form, 'Could not add item to cart. Please try again.');
      })
      .then(function () {
        setSubmitting(form, false);
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
    if (isClearCartEnabled(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    void addToCart(form);
  }, true);
})();
